import {
  AppointmentUpdateData,
  StepUpdateData,
  TreatmentPlanAppointment,
} from "../services/treatmentPlanService";
import { EditableAppointmentData } from "../types/editableTreatmentPlan";

/** Converts appointments to the PUT body format */
export function buildAppointmentUpdateData(
  appointments: TreatmentPlanAppointment[],
): AppointmentUpdateData[] {
  return [...appointments]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((appt) => ({
      id: appt.id,
      timeframeLabel: appt.timeframeLabel,
      conditionalPath: appt.conditionalPath || undefined,
      description: appt.description || undefined,
      sortOrder: appt.sortOrder,
      totalDurationMinutes: appt.totalDurationMinutes,
      steps: [...appt.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(
          (step): StepUpdateData => ({
            id: step.id,
            name: step.name,
            description: step.description || undefined,
            sortOrder: step.sortOrder,
            durationMinutes: step.durationMinutes,
            fee: step.fee,
            isFeeEdited: step.isFeeEdited,
            teeth: step.teeth,
            surfaces: step.surfaces,
            codeSuggestions: step.codeSuggestions.map((cs) => ({
              id: cs.id,
              cdtCodeId: cs.cdtCodeId,
            })),
          }),
        ),
    }));
}

export function convertToEditableAppointment(
  apiAppointment: TreatmentPlanAppointment,
): EditableAppointmentData {
  const computedDuration = apiAppointment.steps.reduce(
    (sum, step) => sum + (step.durationMinutes ?? 20),
    0,
  );

  return {
    id: apiAppointment.id,
    timeframeValue: apiAppointment.timeframeLabel || "",
    timeframeUnit: "",
    conditionalPath: apiAppointment.conditionalPath || "",
    description: apiAppointment.description || "",
    sortOrder: apiAppointment.sortOrder,
    totalDurationMinutes:
      apiAppointment.totalDurationMinutes ?? computedDuration,
    isDurationEdited:
      apiAppointment.totalDurationMinutes != null &&
      apiAppointment.totalDurationMinutes !== computedDuration,
    procedures: apiAppointment.steps.map((step) => {
      const explanation = step.codeSuggestions
        .map((cs) => cs.description)
        .filter(Boolean)
        .join("\n");
      const defaultFee = step.codeSuggestions.reduce(
        (sum, cs) => sum + (cs.defaultFee || 0),
        0,
      );
      const codeSuggestionCodes = step.codeSuggestions.map((cs) => cs.code);

      return {
        id: step.id,
        text: step.name,
        description: step.description || "",
        sortOrder: step.sortOrder,
        cdtCodes: codeSuggestionCodes,
        customProcedureId: undefined,
        resolvedCdtCodes: codeSuggestionCodes,
        explanation,
        teeth: step.teeth ?? [],
        surfaces: step.surfaces ?? [],
        durationMinutes: step.durationMinutes ?? 20,
        fee: String(step.fee ?? defaultFee),
        isFeeEdited: step.isFeeEdited ?? false,
      };
    }),
  };
}
